import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios with global timeout
axios.defaults.timeout = 10000; // 10 seconds for public endpoints

// Create separate axios instance for admin API calls (needs longer timeout)
const adminAxios = axios.create({
  timeout: 30000, // 30 seconds for admin endpoints
  baseURL: API
});

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Retry helper for failed requests
const retryRequest = async (requestFn, retries = 2, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === retries - 1) throw error;
      console.warn(`Request failed, retrying (${i + 1}/${retries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Dossiers
export const getDossiers = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/dossiers?${params}`, {
    timeout: 5000  // 5 seconds - should be fast with cache
  });
  return response.data;
};

export const getPopularDossiers = async () => {
  const response = await axios.get(`${API}/dossiers?sort_by=popular&status=active`);
  return response.data;
};

export const getLatestDossiers = async () => {
  const response = await axios.get(`${API}/dossiers?sort_by=latest&status=active`);
  return response.data;
};

export const getDraftDossiers = async () => {
  const response = await axios.get(`${API}/dossiers/drafts/list`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get published articles for editing
export const getPublishedDossiers = async () => {
  const response = await axios.get(`${API}/dossiers/published/list`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get featured article (algorithm or manual)
export const getFeaturedDossier = async () => {
  const response = await axios.get(`${API}/dossiers/featured`);
  return response.data;
};

// Set article as featured/main
export const setDossierFeatured = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/set-featured`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Remove featured status
export const unsetDossierFeatured = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/unset-featured`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Set as breaking news
export const setDossierBreaking = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/set-breaking`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Remove breaking news status
export const unsetDossierBreaking = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/unset-breaking`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDossier = async (dossierId) => {
  const response = await axios.get(`${API}/dossiers/${dossierId}`, {
    timeout: 5000  // 5 seconds - should be fast with cache
  });
  return response.data;
};

// Alias for compatibility
export const getDossierById = getDossier;

// Track view for popularity
export const trackDossierView = async (dossierId) => {
  try {
    await axios.post(`${API}/dossiers/${dossierId}/view`);
  } catch (e) { /* ignore */ }
};

// Publish draft
export const publishDossier = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Regenerate image with optional custom prompt
export const regenerateDossierImage = async (dossierId, customPrompt = null) => {
  const body = customPrompt ? { custom_prompt: customPrompt } : {};
  const response = await axios.post(`${API}/dossiers/${dossierId}/regenerate-image`, body, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

// Regenerate image with optional reference images (multipart form)
export const regenerateDossierImageWithRefs = async (dossierId, customPrompt = null, referenceImages = []) => {
  const formData = new FormData();
  
  if (customPrompt) {
    formData.append('custom_prompt', customPrompt);
  }
  
  referenceImages.forEach((file, index) => {
    formData.append('reference_images', file);
  });
  
  const headers = getAuthHeader();
  // Don't set Content-Type for FormData, let browser set it with boundary
  delete headers['Content-Type'];
  
  const response = await axios.post(`${API}/dossiers/${dossierId}/regenerate-image-with-refs`, formData, {
    headers,
    timeout: 120000
  });
  return response.data;
};

// Get AI suggestion for image prompt
export const suggestImagePrompt = async (dossierId) => {
  const response = await axios.get(`${API}/dossiers/${dossierId}/suggest-image-prompt`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Edit with AI observation
export const editDossierWithAI = async (dossierId, observation) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/edit-with-ai`, 
    { observation },
    { headers: getAuthHeader(), timeout: 120000 }
  );
  return response.data;
};

// Get AI suggestions for article improvement
export const getArticleSuggestions = async (dossierId) => {
  const response = await axios.get(`${API}/dossiers/${dossierId}/suggestions`, 
    { headers: getAuthHeader(), timeout: 60000 }
  );
  return response.data;
};

// Chat with article - contextual AI
export const chatWithArticle = async (dossierId, message) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/chat`, 
    { dossier_id: dossierId, message },
    { 
      headers: getAuthHeader(),
      timeout: 60000
    }
  );
  return response.data;
};

export const createDossier = async (data) => {
  const response = await axios.post(`${API}/dossiers`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDossier = async (dossierId, data) => {
  const response = await axios.put(`${API}/dossiers/${dossierId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Weekly Editions
export const getEditions = async (status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/editions${params}`);
  return response.data;
};

export const getEdition = async (editionId) => {
  const response = await axios.get(`${API}/editions/${editionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Alias for compatibility
export const getEditionById = getEdition;

export const getLatestEdition = async () => {
  const response = await axios.get(`${API}/editions/latest/published`);
  return response.data;
};

export const createEdition = async (data) => {
  const response = await axios.post(`${API}/editions`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEdition = async (editionId, data) => {
  const response = await axios.put(`${API}/editions/${editionId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Evidence
export const getEvidence = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/evidence?${params}`);
  return response.data;
};

export const getEvidenceItem = async (evidenceId) => {
  const response = await axios.get(`${API}/evidence/${evidenceId}`);
  return response.data;
};

// Alias for compatibility
export const getEvidenceById = getEvidenceItem;

export const createEvidence = async (data) => {
  const response = await axios.post(`${API}/evidence`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEvidence = async (evidenceId, data) => {
  const response = await axios.put(`${API}/evidence/${evidenceId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteEvidence = async (evidenceId) => {
  const response = await axios.delete(`${API}/evidence/${evidenceId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Influence Index
export const getInfluenceIndex = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/influence-index?${params}`);
  return response.data;
};

export const getInfluenceEntry = async (entryId) => {
  const response = await axios.get(`${API}/influence-index/${entryId}`);
  return response.data;
};

export const createInfluenceEntry = async (data) => {
  const response = await axios.post(`${API}/influence-index`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateInfluenceEntry = async (entryId, data) => {
  const response = await axios.put(`${API}/influence-index/${entryId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteInfluenceEntry = async (entryId) => {
  const response = await axios.delete(`${API}/influence-index/${entryId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Dossier delete
export const deleteDossier = async (dossierId) => {
  const response = await axios.delete(`${API}/dossiers/${dossierId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Edition delete
export const deleteEdition = async (editionId) => {
  const response = await axios.delete(`${API}/editions/${editionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Chat
export const sendChatMessage = async (data) => {
  const response = await axios.post(`${API}/chat`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// AI Agents
export const runDossierBuilder = async (data) => {
  const response = await axios.post(`${API}/agents/dossier-builder`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runRoleViews = async (data) => {
  const response = await axios.post(`${API}/agents/role-views`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runClaimExtractor = async (data) => {
  const response = await axios.post(`${API}/agents/claim-extractor`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runWeeklyPlanner = async (data) => {
  const response = await axios.post(`${API}/agents/weekly-planner`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runAudioScript = async (data) => {
  const response = await axios.post(`${API}/agents/audio-script`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Audio
export const generateAudio = async (data) => {
  const response = await axios.post(`${API}/audio/generate`, data, {
    headers: getAuthHeader(),
    responseType: 'blob'
  });
  return response.data;
};

// Subscription
export const createCheckoutSession = async (planId) => {
  const response = await axios.post(`${API}/subscription/checkout`, {
    plan_id: planId,
    origin_url: window.location.origin
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// User Actions
export const saveDossier = async (dossierId) => {
  const response = await axios.post(`${API}/user/save-dossier/${dossierId}`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const unsaveDossier = async (dossierId) => {
  const response = await axios.delete(`${API}/user/save-dossier/${dossierId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getSavedItems = async () => {
  const response = await axios.get(`${API}/user/saved-items`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Editorial Tasks
export const getEditorialTasks = async (status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/editorial-tasks${params}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createEditorialTask = async (data) => {
  const response = await axios.post(`${API}/editorial-tasks`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEditorialTask = async (taskId, data) => {
  const response = await axios.put(`${API}/editorial-tasks/${taskId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Search
export const globalSearch = async (query) => {
  const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

// Stats
export const getStats = async () => {
  const response = await axios.get(`${API}/stats`);
  return response.data;
};

// Scraping
export const runScrapingNow = async () => {
  const response = await axios.post(`${API}/scraping/run`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getScrapingStatus = async () => {
  const response = await axios.get(`${API}/scraping/status`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const toggleScraping = async (enable) => {
  const response = await axios.post(`${API}/scraping/toggle`, { enable }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getScrapingHistory = async (limit = 20) => {
  const response = await axios.get(`${API}/scraping/history?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Staff Management
export const getStaffMembers = async () => {
  const response = await axios.get(`${API}/staff`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createStaffMember = async (data) => {
  const response = await axios.post(`${API}/staff`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateStaffMember = async (userId, data) => {
  const response = await axios.put(`${API}/staff/${userId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteStaffMember = async (userId) => {
  const response = await axios.delete(`${API}/staff/${userId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllUsers = async (userType = null) => {
  const params = userType ? `?user_type=${userType}` : '';
  const response = await axios.get(`${API}/users${params}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Content Generation
export const generateDossier = async (data) => {
  const response = await axios.post(`${API}/generate/dossier`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const generateEdition = async (data) => {
  const response = await axios.post(`${API}/generate/edition`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const generateInfluenceEntry = async (data) => {
  const response = await axios.post(`${API}/generate/influence`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const publishContent = async (contentType, contentId) => {
  const response = await axios.post(`${API}/content/${contentType}/${contentId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDraftContent = async () => {
  const response = await axios.get(`${API}/content/drafts`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Manual Scraping Flow
export const manualScrapeSearch = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/search`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const manualAdaptContent = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/adapt`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const manualPublishArticles = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/publish`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ==================== X MONITOR & STORIES ====================

// Stories
export const getStories = async (status = 'ACTIVE') => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/stories?status=${status}`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

export const getStoryDetail = async (storyId) => {
  const response = await axios.get(`${API}/stories/${storyId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createStory = async (data) => {
  const response = await axios.post(`${API}/stories`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateStory = async (storyId, data) => {
  const response = await axios.patch(`${API}/stories/${storyId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const addStoryUpdate = async (storyId, data) => {
  const response = await axios.post(`${API}/stories/${storyId}/updates`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pending Approvals
export const getPendingApprovals = async (status = 'PENDING', limit = 50, page = 1, filters = {}) => {
  return retryRequest(async () => {
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
      page: page.toString()
    });
    
    // Add filters if present
    if (filters.account && filters.account !== 'all') params.append('account', filters.account);
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    if (filters.scoreMin !== undefined && filters.scoreMin !== '') params.append('score_min', filters.scoreMin);
    if (filters.scoreMax !== undefined && filters.scoreMax !== '') params.append('score_max', filters.scoreMax);
    if (filters.sortBy) params.append('sort_by', filters.sortBy);
    if (filters.sortOrder) params.append('sort_order', filters.sortOrder);
    
    const response = await adminAxios.get(`/pending-approvals?${params.toString()}`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

// Bulk Process Approvals - Approve or Reject multiple at once
export const bulkProcessApprovals = async (approvalIds, action) => {
  const response = await axios.post(`${API}/pending-approvals/bulk-action`, 
    { approval_ids: approvalIds, action: action },
    {
      headers: getAuthHeader(),
      timeout: 10000  // 10 seconds - backend returns immediately, generation happens in background
    }
  );
  return response.data;
};

// Process Approval - Approve or Reject (article generation happens in background)
export const processApproval = async (approvalId, action) => {
  const response = await axios.post(`${API}/pending-approvals/${approvalId}/action`, action, {
    headers: getAuthHeader(),
    timeout: 5000  // 5 seconds - backend returns immediately, generation happens in background
  });
  return response.data;
};

// X Monitor
export const runXMonitor = async () => {
  const response = await axios.post(`${API}/x-monitor/run`, {}, {
    headers: getAuthHeader(),
    timeout: 120000  // 2 minute timeout for monitoring
  });
  return response.data;
};

export const getXMonitorStatus = async () => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/x-monitor/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

export const getIngestedTweets = async (status = 'all') => {
  const response = await axios.get(`${API}/tweets-ingested?status=${status}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// X Monitor Accounts Management
export const updateMonitoredAccounts = async (accounts) => {
  const response = await axios.put(`${API}/x-monitor/accounts`, { accounts }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const addMonitoredAccount = async (account, autoPublish = false) => {
  const response = await axios.post(`${API}/x-monitor/accounts/add`, { account, auto_publish: autoPublish }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const removeMonitoredAccount = async (account) => {
  const response = await axios.delete(`${API}/x-monitor/accounts/${account}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Toggle auto-publish for an account
export const toggleAutoPublish = async (account, autoPublish) => {
  const response = await axios.put(`${API}/x-monitor/accounts/${account}/auto-publish`, 
    { auto_publish: autoPublish }, 
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Get auto-publish activity log
export const getAutoPublishLog = async (limit = 50) => {
  const response = await axios.get(`${API}/x-monitor/auto-publish/log?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get auto-publish stats
export const getAutoPublishStats = async () => {
  const response = await axios.get(`${API}/x-monitor/auto-publish/stats`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pause X Monitor
export const pauseXMonitor = async () => {
  const response = await adminAxios.post(`/x-monitor/pause`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Resume X Monitor
export const resumeXMonitor = async () => {
  const response = await adminAxios.post(`/x-monitor/resume`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Set X Monitor Interval
export const setXMonitorInterval = async (interval) => {
  const response = await adminAxios.post(`/x-monitor/interval`, { interval }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ======================= DATA MIGRATION =======================

// Export all data to JSON
export const exportData = async () => {
  const response = await axios.get(`${API}/admin/export-data`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Import data from JSON
export const importData = async (data, mode = 'merge') => {
  const response = await axios.post(`${API}/admin/import-data?mode=${mode}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get download URL for export
export const getExportDownloadUrl = () => {
  return `${API}/admin/export-data/download`;
};

// ======================= SITE CONFIGURATION =======================

// Get site config (public - no auth needed for categories, navigation, footer, seo)
export const getSiteConfig = async (configType) => {
  const response = await axios.get(`${API}/config/${configType}`);
  return response.data;
};

// Update site config (admin only)
export const updateSiteConfig = async (configType, data) => {
  const response = await axios.put(`${API}/config/${configType}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Reset site config to defaults (admin only)
export const resetSiteConfig = async (configType) => {
  const response = await axios.post(`${API}/config/${configType}/reset`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Add category
export const addCategory = async (category) => {
  const response = await axios.post(`${API}/config/categories/add`, category, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete category
export const deleteCategory = async (categoryId) => {
  const response = await axios.delete(`${API}/config/categories/${categoryId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Publish edition
export const publishEdition = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Unpublish edition
export const unpublishEdition = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/unpublish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Suggest edition content with AI
export const suggestEditionContent = async (data) => {
  const response = await axios.post(`${API}/editions/suggest-content`, data, {
    headers: getAuthHeader(),
    timeout: 60000
  });
  return response.data;
};

// Suggest footer links with AI
export const suggestFooterLinks = async (data) => {
  const response = await axios.post(`${API}/config/footer/suggest-links`, data, {
    headers: getAuthHeader(),
    timeout: 60000
  });
  return response.data;
};

// ======================= STATIC PAGES =======================

// Get all static pages
export const getStaticPages = async (publishedOnly = false) => {
  const params = publishedOnly ? '?published_only=true' : '';
  const response = await axios.get(`${API}/pages${params}`);
  return response.data;
};

// Get single static page by slug
export const getStaticPage = async (slug) => {
  const response = await axios.get(`${API}/pages/${slug}`);
  return response.data;
};

// Create static page
export const createStaticPage = async (data) => {
  const response = await axios.post(`${API}/pages`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Update static page
export const updateStaticPage = async (slug, data) => {
  const response = await axios.put(`${API}/pages/${slug}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete static page
export const deleteStaticPage = async (slug) => {
  const response = await axios.delete(`${API}/pages/${slug}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Publish static page
export const publishStaticPage = async (slug) => {
  const response = await axios.post(`${API}/pages/${slug}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Unpublish static page
export const unpublishStaticPage = async (slug) => {
  const response = await axios.post(`${API}/pages/${slug}/unpublish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Generate page content with AI
export const generatePageContent = async (data) => {
  const response = await axios.post(`${API}/pages/generate-content`, data, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

